'use client';
import { useState, useEffect, useRef, ElementType, ComponentPropsWithoutRef } from 'react';

type TextMoreProps<T extends ElementType = 'p'> = {
  as?: T;
  lines?: number;
  className?: string;
  children: React.ReactNode;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'lines' | 'className' | 'children'>;

const lineClampClasses: Record<number, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
  24: 'line-clamp-24',
};

export function TextMore<T extends ElementType = 'p'>({
  as,
  lines = 3,
  className = '',
  children,
  ...props
}: TextMoreProps<T>) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef<HTMLElement | null>(null);
  const Component = as || 'p';

  useEffect(() => {
    const checkClamp = () => {
      if (textRef.current) {
        setIsClamped(textRef.current.scrollHeight > textRef.current.clientHeight);
      }
    };

    checkClamp();
    window.addEventListener('resize', checkClamp);
    return () => window.removeEventListener('resize', checkClamp);
  }, [children]);

  const clampClass = lineClampClasses[lines] || 'line-clamp-3';
  const combinedClassName = [!isExpanded && clampClass, className].filter(Boolean).join(' ');

  return (
    <div>
      <Component
        ref={(el: HTMLElement | null) => {
          textRef.current = el;
        }}
        className={combinedClassName}
        {...props}
        style={
          !isExpanded
            ? {
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: lines,
              }
            : {}
        }
      >
        {children}
      </Component>
      {!isExpanded && isClamped && (
        <button
          onClick={() => setIsExpanded(true)}
          className="text-base underline"
          aria-label="Show full text"
          style={{ color: 'rgb(64, 166, 255)' }}
        >
          more
        </button>
      )}
    </div>
  );
}
